/* ============================================================
   OVERLAY — the single BumpRunner scene, as PURE derivation.
   ------------------------------------------------------------
   There is ONE overlay canvas. What it shows each tick is derived,
   never imperatively pushed: a masking cover (channel bumper / ad)
   always wins over the transparent program ident. The KEY encodes
   the IDENTITY of what to show — the tune that owns the bumper (so a
   new channel change → new key → fresh bumper, but a mid-bump re-tick
   keeps the SAME key and doesn't restart), the ad slot, or the
   announced video. The renderer reconciles to the key declaratively;
   no per-call cache resets.
   ============================================================ */
import type { CoverKind } from './cover'

export type OverlayKind = 'channel' | 'ad' | 'ident' | null

export interface OverlayInputs {
  /** The masking cover this tick (from deriveCover): 'channel' | 'ad' | null. */
  cover: CoverKind
  /** Whether a timed program ident is currently active. */
  identActive: boolean
  /** Current channel number (part of the channel-bumper key). */
  channelNumber: number
  /** The active tune's start wall-clock (changes ⇒ a fresh bumper). Undefined when not tuning. */
  tuneAt: number | undefined
  /** The desired ad's queue index, or null when the desired frame isn't an ad. */
  adIndex: number | null
  /** The video id the active ident announces. */
  identVideoId: string | null
  /** The full-screen content is a COMPONENT (rendered by the React component layer, not the 3D bumper).
   *  When true, an 'ad'-cover does NOT raise a bumper scene — the component layer owns the picture. */
  componentContent?: boolean
}

/** Derive the overlay scene to show this tick + its identity key. A cover wins over the ident; the
 *  key changes only when the THING to show changes (new tune / new ad slot / new announced video). */
export const deriveOverlay = (i: OverlayInputs): { kind: OverlayKind; key: string | null } => {
  const kind: OverlayKind =
    i.cover === 'channel' ? 'channel'
    : i.cover === 'ad' ? (i.componentContent ? null : 'ad') // component content has no bumper scene to show
    : i.identActive ? 'ident' : null
  const key: string | null =
    kind === 'channel' ? `channel:${i.channelNumber}:${i.tuneAt}`
    : kind === 'ad' ? `ad:${i.adIndex ?? ''}`
    : kind === 'ident' ? `ident:${i.identVideoId}`
    : null
  return { kind, key }
}

export interface IdentFireInputs {
  cover: CoverKind
  desiredKind: 'off' | 'video' | 'scene' | 'component'
  onTarget: boolean
  /** The foreground frame's video id (genuinely up). */
  fgId: string | null
  /** The desired video id. */
  desiredId: string | null
  /** The video id of the last ident we fired (edge-guard so we announce once per new video). */
  lastInterstitialVideo: string | null
}

/** Should we fire the program ident this tick? True only on the EDGE of a new in-channel video
 *  genuinely up with NO masking cover — once per video. */
export const shouldFireIdent = (i: IdentFireInputs): boolean =>
  i.cover === null &&
  i.desiredKind === 'video' &&
  i.onTarget &&
  i.fgId !== null &&
  i.fgId === i.desiredId &&
  i.fgId !== i.lastInterstitialVideo
